import React from 'react'

interface FeatureItemProps {
  type: 'feature' | 'improvement' | 'fix'
  description: React.ReactNode | string
  icon?: string
  admin?: boolean
}

const FeatureItem: React.FC<FeatureItemProps> = React.memo(
  ({ type, description, icon, admin = false }) => {
    const getTypeClass = () => {
      switch (type) {
        case 'feature':
          return 'changelog-feature'
        case 'improvement':
          return 'changelog-improvement'
        case 'fix':
          return 'changelog-bug-fix'
        default:
          return ''
      }
    }

    const getThemeStyles = () => {
      const baseStyles = {
        feature: {
          backgroundColor: 'var(--changelog-feature-bg)',
          borderColor: 'var(--changelog-feature-border)',
          color: 'var(--changelog-feature-text)',
        },
        improvement: {
          backgroundColor: 'var(--changelog-improvement-bg)',
          borderColor: 'var(--changelog-improvement-border)',
          color: 'var(--changelog-improvement-text)',
        },
        fix: {
          backgroundColor: 'var(--changelog-fix-bg)',
          borderColor: 'var(--changelog-fix-border)',
          color: 'var(--changelog-fix-text)',
        },
        default: {
          backgroundColor: 'var(--changelog-filter-bg)',
          borderColor: 'var(--changelog-border)',
          color: 'var(--changelog-text)',
        },
      }

      const typeStyle = baseStyles[type] || baseStyles.default
      return typeStyle
    }

    const getBadgeStyles = () => {
      const baseStyles = {
        feature: {
          backgroundColor: 'var(--changelog-feature-badge)',
          color: 'var(--changelog-feature-text)',
        },
        improvement: {
          backgroundColor: 'var(--changelog-improvement-badge)',
          color: 'var(--changelog-improvement-text)',
        },
        fix: {
          backgroundColor: 'var(--changelog-fix-badge)',
          color: 'var(--changelog-fix-text)',
        },
        default: {
          backgroundColor: 'var(--changelog-filter-bg)',
          color: 'var(--changelog-text)',
        },
      }

      const typeStyle = baseStyles[type] || baseStyles.default

      return typeStyle
    }

    const getTypeLabel = () => {
      switch (type) {
        case 'feature':
          return 'feature'
        case 'improvement':
          return 'improvement'
        case 'fix':
          return 'bug-fix'
        default:
          return type
      }
    }

    return (
      <p
        className={`changelog-update-description ${getTypeClass()} js-changelog-update-description border rounded-lg p-3 mb-2 flex items-center`}
        data-category={getTypeLabel()}
        data-admin={admin}
        style={{
          ...getThemeStyles(),
          borderWidth: '1px',
          borderStyle: 'solid',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          position: 'relative',
        }}
      >
        {admin && (
          <div
            className="admin-indicator"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: 'var(--changelog-admin-icon)',
              color: 'var(--changelog-admin-bg)',
              fontSize: '12px',
              fontWeight: 'bold',
              flexShrink: 0,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
            title="Admin Only"
          >
            <i className="fas fa-user-secret" />
          </div>
        )}
        <span
          className="changelog-type text-sm px-2 py-1 rounded flex-shrink-0"
          style={{
            ...getBadgeStyles(),
            width: admin ? '130px' : '115px', // Wider for admin badge
            textAlign: 'center',
            boxSizing: 'border-box',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {getTypeLabel()}
        </span>
        <span
          className="flex-grow"
          style={{ color: 'var(--changelog-text)', fontSize: '18.4px' }}
        >
          {description}
        </span>
      </p>
    )
  },
)

export default FeatureItem
